/**
 * @copyright Copyright 2023-2024 Epic Systems Corporation
 * @file Category tag display for the showroom listing page
 * @module Epic.AppOrchard.Showroom.DownloadInfoSection
 */

import { StarIcon } from "@chakra-ui/icons";
import { Tag, TagLabel, TagLeftIcon, Wrap } from "@chakra-ui/react";
import { getCategory } from "ao/components/Showroom/Helper";
import { ICategory } from "ao/types/showroom";
import React, { FunctionComponent, memo, useMemo } from "react";

interface IProps {
	categoryIds: number[];
	categories: ICategory[];
	primaryCategoryId?: number;
	color?: string;
}

export const CategoryTags: FunctionComponent<IProps> = memo((props) => {
	const { categoryIds, categories, primaryCategoryId, color } = props;

	const nodes = useMemo(() => {
		let sortedCategories = categoryIds.map((id) => getCategory(id, categories)).filter((x) => x !== null);

		//sport alphabetically with primary category in the beginning
		sortedCategories = sortedCategories.sort((a, b) =>
			a?.id === primaryCategoryId ? -1 : a!.name.localeCompare(b!.name),
		);
		return sortedCategories;
	}, [categories, categoryIds, primaryCategoryId]);

	const tagProps = color
		? { variant: "unset", color: color, border: `1px solid ${color}` }
		: { variant: "subtle", color: "showroomPrimary", border: `1px solid showroomPrimary` };

	return (
		<Wrap spacing={2} marginY="7px" justify={{ base: "center", lg: "left" }} pt="10px">
			{nodes.map((node) => (
				<Tag size="md" key={node!.id} borderRadius="full" fontWeight="normal" {...tagProps}>
					{primaryCategoryId !== undefined && primaryCategoryId === node?.id && (
						<TagLeftIcon boxSize="12px" as={StarIcon} title="primary category" />
					)}
					<TagLabel>{node!.name}</TagLabel>
				</Tag>
			))}
		</Wrap>
	);
});
